import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import ReceptionistForm from './ReceptionistForm';
import { fetchBranches } from '../../../services/gym/catalogApi';
import {
  deleteReceptionist,
  fetchReceptionists,
  saveReceptionist,
} from '../../../services/receptionist/receptionistApi';
import { usePanelToast } from '../../../context/PanelToastContext';
import { instantLoadState } from '../../../utils/instantLoad';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import { matchSaleDateRange } from '../../receptionist/sales/saleHistoryFilters';
import {
  exportReceptionistCsv,
  exportReceptionistExcel,
  exportReceptionistPdf,
  exportReceptionists,
  printReceptionistList,
} from './exportReceptionists';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../packages/packages.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './receptionists.css';

function getReceptionistStatusLabel(status) {
  return status === 'active' ? 'Active' : 'Inactive';
}

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function LoginBadge({ hasLogin }) {
  return (
    <span className={`owner-badge ${hasLogin ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {hasLogin ? 'Yes' : 'No'}
    </span>
  );
}

function ReceptionistManagement() {
  const toast = usePanelToast();
  const receptionistBootstrap = instantLoadState('receptionists');
  const [receptionistList, setReceptionistList] = useState(receptionistBootstrap.initialData);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(receptionistBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: '',
    email: '',
    phone: '',
    branch: '',
    shift: '',
    login: '',
    status: '',
  });

  const loadData = useCallback(async () => {
    try {
      setError('');
      if (!receptionistList.length) setLoading(true);
      const [receptionists, branches] = await Promise.all([
        fetchReceptionists(),
        fetchBranches(),
      ]);
      setReceptionistList(receptionists);
      setBranchOptions(
        (branches || [])
          .filter((b) => b.status === 'active')
          .map((b) => ({ id: b.id, name: b.name })),
      );
    } catch (err) {
      setError(err.message || 'Failed to load receptionists');
    } finally {
      setLoading(false);
    }
  }, [receptionistList.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredReceptionists = useMemo(() => (
    receptionistList.filter((receptionist) => {
      const rowDate = receptionist.createdAt || receptionist.joinedDate || receptionist.date || '';
      const dateOk = !rowDate || matchSaleDateRange(rowDate, rangeStart, rangeEnd);
      return (
        dateOk
        && matchTextFilter(receptionist.name, filters.name)
        && matchTextFilter(receptionist.email, filters.email)
        && matchTextFilter(receptionist.phone, filters.phone)
        && matchTextFilter(receptionist.branch, filters.branch)
        && matchTextFilter(receptionist.shift, filters.shift)
        && matchTextFilter(receptionist.hasLogin ? 'Yes' : 'No', filters.login)
        && matchTextFilter(getReceptionistStatusLabel(receptionist.status), filters.status)
      );
    })
  ), [receptionistList, filters, rangeStart, rangeEnd]);

  const openAddModal = () => {
    setFormKey((k) => k + 1);
    setShowAddModal(true);
  };

  const closeAddModal = () => setShowAddModal(false);

  const openEditModal = (receptionist) => {
    setFormKey((k) => k + 1);
    setEditTarget(receptionist);
  };

  const closeEditModal = () => setEditTarget(null);

  const handleAddSubmit = async (form) => {
    try {
      setError('');
      const created = await saveReceptionist(form);
      setReceptionistList((prev) => {
        if (prev.some((r) => r.id === created.id)) {
          return prev.map((r) => (r.id === created.id ? { ...created, authWarning: undefined } : r));
        }
        return [...prev, { ...created, authWarning: undefined }];
      });
      closeAddModal();
      toast.showAdded('Receptionist');
      if (created.authWarning) {
        toast.showError(
          `${created.authWarning} The receptionist was saved — link login manually in Supabase if needed.`,
          'Login not created',
        );
      } else if (!form.password?.trim()) {
        toast.showSuccess(
          'Receptionist saved without login. Add a password later to create their sign-in.',
          'Added successfully',
        );
      }
    } catch (err) {
      const msg = err.message || 'Failed to add receptionist';
      setError(msg);
      toast.showError(msg);
    }
  };

  const handleEditSubmit = async (form) => {
    if (!editTarget) return;
    try {
      const updated = await saveReceptionist(form, editTarget.id);
      setReceptionistList((prev) =>
        prev.map((r) => (r.id === editTarget.id ? { ...updated, authWarning: undefined } : r)),
      );
      closeEditModal();
      if (updated.authWarning) {
        toast.showError(updated.authWarning, 'Login not created');
      } else if (updated.passwordChanged) {
        toast.showSuccess('They can sign in with the new password.', 'Password updated');
      } else if (form.password?.trim()) {
        toast.showSuccess('Receptionist login is ready. They can sign in now.', 'Login created');
      } else {
        toast.showUpdated('Receptionist');
      }
    } catch (err) {
      const msg = err.message || 'Failed to update receptionist';
      setError(msg);
      toast.showError(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteReceptionist(deleteTarget.id);
      setReceptionistList((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.showDeleted('Receptionist');
    } catch (err) {
      setError(err.message || 'Failed to delete receptionist');
    }
  };

  return (
    <div>
      <PageHeader title="Receptionists" subtitle="Manage front desk staff and branch assignments">
        <button type="button" className="owner-btn owner-btn-primary" onClick={openAddModal}>
          Add Receptionist
        </button>
      </PageHeader>

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => printReceptionistList(filteredReceptionists)}
        onCsv={() => exportReceptionistCsv(filteredReceptionists)}
        onPdf={() => exportReceptionistPdf(filteredReceptionists)}
        onExcel={() => exportReceptionistExcel(filteredReceptionists)}
        onExport={() => exportReceptionists(filteredReceptionists)}
        ariaLabel="Receptionist print and export"
      />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading receptionists...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Email"
                  filterKey="email"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search email..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Shift"
                  filterKey="shift"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search shift..."
                />
                <ColumnFilterHeader
                  label="Login"
                  filterKey="login"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search yes/no..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReceptionists.length > 0 ? (
                filteredReceptionists.map((receptionist) => (
                  <tr key={receptionist.id}>
                    <td>{receptionist.name}</td>
                    <td>{receptionist.email}</td>
                    <td>{receptionist.phone}</td>
                    <td>{receptionist.branch}</td>
                    <td>{receptionist.shift}</td>
                    <td><LoginBadge hasLogin={receptionist.hasLogin} /></td>
                    <td><StatusBadge status={receptionist.status} /></td>
                    <td>
                      <div className="receptionist-actions-cell">
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => openEditModal(receptionist)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="owner-btn owner-btn-sm receptionist-btn-danger"
                          onClick={() => setDeleteTarget(receptionist)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No receptionists found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="subscription-modal-overlay" onClick={closeAddModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-receptionist-title"
          >
            <div className="subscription-modal-header">
              <h2 id="add-receptionist-title" className="subscription-modal-title">Add Receptionist</h2>
              <button type="button" className="subscription-modal-close" onClick={closeAddModal} aria-label="Close">×</button>
            </div>
            <ReceptionistForm
              key={formKey}
              isNew
              branchOptions={branchOptions}
              submitLabel="Add Receptionist"
              onSubmit={handleAddSubmit}
              onCancel={closeAddModal}
            />
          </div>
        </div>
      )}

      {editTarget && (
        <div className="subscription-modal-overlay" onClick={closeEditModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-receptionist-title"
          >
            <div className="subscription-modal-header">
              <h2 id="edit-receptionist-title" className="subscription-modal-title">Edit Receptionist</h2>
              <button type="button" className="subscription-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
            </div>
            <ReceptionistForm
              key={formKey}
              branchOptions={branchOptions}
              initialData={editTarget}
              needsLoginSetup={!editTarget.hasLogin}
              submitLabel="Save Changes"
              onSubmit={handleEditSubmit}
              onCancel={closeEditModal}
            />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Receptionist</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="receptionist-modal-message">
              Delete receptionist <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn receptionist-btn-danger" onClick={handleDelete}>Delete</button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceptionistManagement;
